import { useState, useEffect } from 'react';
import { playersApi, paymentsApi, expensesApi, configApi } from '../services/api';
import { isSupabaseAvailable } from '../lib/supabase';
import { Player, Payment, Expense, AppConfiguration } from '../types';

// Generic database hook for any data type
export function useDatabase<T>(
  apiService: {
    getAll: () => Promise<T[]>;
    create?: (item: Omit<T, 'id'>) => Promise<T>;
    update?: (id: string, updates: Partial<T>) => Promise<T>;
    delete?: (id: string) => Promise<void>;
  }
): [T[], (data: T[]) => Promise<void>, boolean, string | null, () => Promise<void>] {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data function
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is available before making calls
      if (!isSupabaseAvailable) {
        throw new Error('Database configuration not available');
      }

      const result = await apiService.getAll();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Database error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update data function - writes changes to database immediately
  const updateData = async (newData: T[]) => {
    try {
      setError(null);

      // If not using database, just update local state
      if (!isSupabaseAvailable) {
        setData(newData);
        return;
      }

      // For database mode, detect and write changes to database
      console.log('Database mode: detecting changes to sync with database');

      // Update local state immediately for UI responsiveness
      setData(newData);

      // Detect changes and write to database
      await syncChangesToDatabase(data, newData);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update data');
      console.error('Database update error:', err);

      // On error, reload from database to restore consistent state
      setTimeout(() => {
        loadData();
      }, 100);
    }
  };

  // Helper function to sync changes to database
  const syncChangesToDatabase = async (oldData: T[], newData: T[]) => {
    if (!apiService.create || !apiService.update || !apiService.delete) {
      console.log('API service missing CRUD operations, skipping database sync');
      return;
    }

    // Create lookup maps for efficient comparison
    const oldMap = new Map(oldData.map(item => [(item as any).id, item]));
    const newMap = new Map(newData.map(item => [(item as any).id, item]));

    // Find items to delete (in old but not in new)
    const toDelete = oldData.filter(item => !newMap.has((item as any).id));

    // Find items to add (in new but not in old, with timestamp-based IDs indicating new items)
    const toAdd = newData.filter(item => {
      const id = (item as any).id;
      return !oldMap.has(id) && /^\d+$/.test(id); // timestamp-based ID pattern
    });

    // Find items to update (in both, but different)
    const toUpdate = newData.filter(item => {
      const id = (item as any).id;
      const oldItem = oldMap.get(id);
      return oldItem && JSON.stringify(item) !== JSON.stringify(oldItem);
    });

    console.log(`Database sync: ${toDelete.length} deletes, ${toAdd.length} adds, ${toUpdate.length} updates`);

    // Execute database operations
    try {
      // Delete items
      for (const item of toDelete) {
        await apiService.delete!((item as any).id);
        console.log(`Deleted item ${(item as any).id}`);
      }

      // Add new items
      for (const item of toAdd) {
        const { id, ...itemWithoutId } = item as any;
        await apiService.create!(itemWithoutId);
        console.log(`Created item ${id}`);
      }

      // Update existing items
      for (const item of toUpdate) {
        const { id, ...updates } = item as any;
        await apiService.update!(id, updates);
        console.log(`Updated item ${id}`);
      }

      console.log('Database sync completed successfully');
    } catch (syncError) {
      console.error('Database sync failed:', syncError);
      throw syncError;
    }
  };

  // Refresh data function
  const refreshData = async () => {
    await loadData();
  };

  return [data, updateData, loading, error, refreshData];
}

// Specialized hooks for each data type
export function usePlayers(): [Player[], (players: Player[]) => Promise<void>, boolean, string | null, () => Promise<void>] {
  return useDatabase(playersApi);
}

export function usePayments(): [Payment[], (payments: Payment[]) => Promise<void>, boolean, string | null, () => Promise<void>] {
  return useDatabase(paymentsApi);
}

export function useExpenses(): [Expense[], (expenses: Expense[]) => Promise<void>, boolean, string | null, () => Promise<void>] {
  return useDatabase(expensesApi);
}

// Special hook for config (single item)
export function useConfig(): [AppConfiguration | null, (config: AppConfiguration) => Promise<void>, boolean, string | null, () => Promise<void>] {
  const [config, setConfig] = useState<AppConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load config function
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is available before making calls
      if (!isSupabaseAvailable) {
        throw new Error('Database configuration not available');
      }

      const result = await configApi.get();
      setConfig(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
      console.error('Config load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load config on mount
  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update config function
  const updateConfig = async (newConfig: AppConfiguration) => {
    try {
      setError(null);
      const updated = await configApi.update(newConfig);
      setConfig(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update configuration');
      console.error('Config update error:', err);
    }
  };

  // Refresh config function
  const refreshConfig = async () => {
    await loadConfig();
  };

  return [config, updateConfig, loading, error, refreshConfig];
}

// Enhanced database operations with auto-refresh hooks
export const createDatabaseOperations = (
  refreshPlayers?: () => Promise<void>,
  refreshPayments?: () => Promise<void>,
  refreshExpenses?: () => Promise<void>
) => ({
  // Player operations
  async addPlayer(player: Omit<Player, 'id'>): Promise<Player> {
    const result = await playersApi.create(player);
    if (refreshPlayers) await refreshPlayers();
    return result;
  },

  async updatePlayer(id: string, updates: Partial<Player>): Promise<Player> {
    if (!playersApi.update) throw new Error('Update operation not supported');
    const result = await playersApi.update(id, updates);
    if (refreshPlayers) await refreshPlayers();
    return result;
  },

  async deletePlayer(id: string): Promise<void> {
    if (!playersApi.delete) throw new Error('Delete operation not supported');
    await playersApi.delete(id);
    if (refreshPlayers) await refreshPlayers();
  },

  // Payment operations
  async addPayment(payment: Omit<Payment, 'id'>): Promise<Payment> {
    if (!paymentsApi.create) throw new Error('Create operation not supported');
    const result = await paymentsApi.create(payment);
    if (refreshPayments) await refreshPayments();
    return result;
  },

  async updatePayment(id: string, updates: Partial<Payment>): Promise<Payment> {
    if (!paymentsApi.update) throw new Error('Update operation not supported');
    const result = await paymentsApi.update(id, updates);
    if (refreshPayments) await refreshPayments();
    return result;
  },

  async deletePayment(id: string): Promise<void> {
    if (!paymentsApi.delete) throw new Error('Delete operation not supported');
    await paymentsApi.delete(id);
    if (refreshPayments) await refreshPayments();
  },

  // Expense operations
  async addExpense(expense: Omit<Expense, 'id'>): Promise<Expense> {
    if (!expensesApi.create) throw new Error('Create operation not supported');
    const result = await expensesApi.create(expense);
    if (refreshExpenses) await refreshExpenses();
    return result;
  },

  async updateExpense(id: string, updates: Partial<Expense>): Promise<Expense> {
    if (!expensesApi.update) throw new Error('Update operation not supported');
    const result = await expensesApi.update(id, updates);
    if (refreshExpenses) await refreshExpenses();
    return result;
  },

  async deleteExpense(id: string): Promise<void> {
    if (!expensesApi.delete) throw new Error('Delete operation not supported');
    await expensesApi.delete(id);
    if (refreshExpenses) await refreshExpenses();
  }
});

// Legacy operations for backward compatibility
export const databaseOperations = createDatabaseOperations();